import { useQuery } from '@tanstack/react-query'
import { createContext, ReactNode, useContext } from 'react'
import { ACCOUNT_PERMISSION } from '../shared/constants'
import { get } from './fetch'

type AccountPermission = (typeof ACCOUNT_PERMISSION)[keyof typeof ACCOUNT_PERMISSION]
type AccountPermissions = Array<AccountPermission>

const AccountPermissionsContext = createContext<AccountPermissions | undefined>(undefined)

export function useAccountPermissions(): AccountPermissions {
	const context = useContext(AccountPermissionsContext)
	if (!context) {
		throw new Error('Cannot use useAccountPermissions outside of AccountPermissionsProvider')
	}
	return context
}

export function useHasAccountPermission(permission?: AccountPermission) {
	const accountPermissions = useAccountPermissions()
	if (!permission) return false
	return accountPermissions.includes(permission)
}

export function AccountPermissionsProvider({ children }: { children: ReactNode }) {
	const { data: accountPermissions, isSuccess } = useQuery({
		queryKey: ['api/me', 'account-permissions'],
		queryFn: () => {
			return get<AccountPermissions>('/api/0/me/account-permissions')
		},
		staleTime: Infinity,
	})

	if (!isSuccess) {
		return null
	}

	return (
		<AccountPermissionsContext.Provider value={accountPermissions}>
			{children}
		</AccountPermissionsContext.Provider>
	)
}
